import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import './CourseCard.css';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
/**
 * Lightweight performance instrumentation (render + global paint) for CourseCard.
 * Enabled when window.__COURSECARD_DEBUG = true or REACT_APP_COURSECARD_DEBUG === 'true'.
 * Exposes window.__courseCardPerf = { [courseId]: { renders, firstRenderTs, lastCommitTs } }.
 */
if (typeof window !== 'undefined' && !window.__courseCardPaintObserverInitialized) {
  const enabled = window.__COURSECARD_DEBUG || process.env.REACT_APP_COURSECARD_DEBUG === 'true';
  if (enabled && 'PerformanceObserver' in window) {
    try {
      const po = new PerformanceObserver(list => {
        list.getEntries().forEach(e => {
          if (e.entryType === 'paint') {
            // eslint-disable-next-line no-console
            console.debug('[CourseCardPerf] global paint', e.name, e.startTime.toFixed(1));
          }
        });
      });
      po.observe({ type: 'paint', buffered: true });
      window.__courseCardPaintObserverInitialized = true;
    } catch (e) {
      // swallow
    }
  }
}

/**
 * CourseCard
 * Compact, self-contained dashboard card summarizing one course.
 * Does NOT depend on existing large components (CourseDetailsSection, UpcomingEvents, etc.).
 * Fetches only what it needs: course meta + limited upcoming events + upcoming assignments.
 *
 * Props:
 *  - courseId (number|string, required)
 *  - maxEvents (default 2)
 *  - maxAssignments (default 6)
 *  - className (optional additional class)
 *  - onLoaded?(dataShape) – optional callback when the card has loaded
 */
function CourseCard({
  courseId,
  maxEvents = 2,
  maxAssignments = 6,
  className = '',
  onLoaded
}) {
  const navigate = useNavigate();
  // Per-section derived state (now sourced from cached async hook outputs)
  const [eventAssignments, setEventAssignments] = useState({});
  // Legacy states removed: loading/error/course/events/assignments now derived

  const courseIdNum = useMemo(() => Number(courseId), [courseId]);
  // Per-render instrumentation (counts component commits) when debug flag enabled.
  const PERF_DEBUG = (typeof window !== 'undefined') && (window.__COURSECARD_DEBUG || process.env.REACT_APP_COURSECARD_DEBUG === 'true');
  const renderCountRef = React.useRef(0);
  renderCountRef.current++;
  useEffect(() => {
    if (!PERF_DEBUG) return;
    const perfStore = window.__courseCardPerf || (window.__courseCardPerf = {});
    const key = String(courseIdNum || 'unknown');
    const entry = perfStore[key] || (perfStore[key] = { renders: 0, firstRenderTs: performance.now(), lastCommitTs: performance.now() });
    entry.renders = renderCountRef.current;
    entry.lastCommitTs = performance.now();
    if (entry.renders === 1) {
      // eslint-disable-next-line no-console
      console.debug('[CourseCardPerf] first render', { courseId: key });
    } else if (entry.renders % 10 === 0) {
      // eslint-disable-next-line no-console
      console.debug('[CourseCardPerf] render milestone', { courseId: key, renders: entry.renders, elapsedMs: (entry.lastCommitTs - entry.firstRenderTs).toFixed(1) });
    }
  }, [PERF_DEBUG, courseIdNum]);

    const { getEndeavor, getEndeavorEvents, getFilteredAssignments, getAssignmentsByEvent, getCachedData, generateCacheKey, markAssignmentCompleted, clearCache } = useData();

  const pickEventDate = useCallback((e) => {
    if (!e || typeof e !== 'object') return null;
    return (
      e.startTime || e.startDate || e.startDatetime || e.startDateTime ||
      e.eventDate || e.date || e.start || e.begin || e.when ||
      // generic scan for keys containing 'date' or 'time' early in object
      (Object.keys(e).find(k=>/(start|event).*?(date|time)/i.test(k)) && e[Object.keys(e).find(k=>/(start|event).*?(date|time)/i.test(k))])
    );
  }, []);

  const pickDueDate = useCallback((a) => {
    const direct = a.dueDate || a.dueDatetime || a.dueDateTime || a.due_datetime || a.due_at || a.dueAt || a.due_date || a.dateDue || a.due || a.deadline || a.dueOn;
    if (direct) return direct;
    const key = Object.keys(a).find(k => /(due|deadline|date|time)/i.test(k));
    return key ? a[key] : null;
  }, []);

  // Local states replacing useCachedAsync
  const [course, setCourse] = useState(undefined);
  const [prevCourse, setPrevCourse] = useState(undefined);
  const [rawEvents, setRawEvents] = useState(undefined);
  const [prevEvents, setPrevEvents] = useState(undefined);
  const [rawAssignments, setRawAssignments] = useState(undefined);
  const [prevAssignments, setPrevAssignments] = useState(undefined);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [refreshingCourse, setRefreshingCourse] = useState(false);
  const [refreshingEvents, setRefreshingEvents] = useState(false);
  const [refreshingAssignments, setRefreshingAssignments] = useState(false);
  const [courseError, setCourseError] = useState(null);
  const [, setEventsError] = useState(null);
  const [, setAssignmentsError] = useState(null);

  // Generic fetch helper for stale-while-revalidate semantics
  const swrFetch = useCallback(async (opts) => {
    const { loadFn, current, setCurrent, setPrev, setLoading, setRefreshing, setErr } = opts;
    if (!loadFn) return;
    const initial = current === undefined;
    setErr(null);
    if (initial) setLoading(true); else setRefreshing(true);
    try {
      const result = await loadFn();
      setPrev(prev => (current !== undefined ? current : (prev !== undefined ? prev : result)));
      setCurrent(result);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  // Effect to (re)load data when courseId changes
  useEffect(() => {
    if (!courseIdNum) return;
    swrFetch({
      loadFn: () => getEndeavor(courseIdNum),
      current: course, setCurrent: setCourse, setPrev: setPrevCourse,
      setLoading: setLoadingCourse, setRefreshing: setRefreshingCourse, setErr: setCourseError
    });
    swrFetch({
      loadFn: () => getEndeavorEvents(courseIdNum),
      current: rawEvents, setCurrent: setRawEvents, setPrev: setPrevEvents,
      setLoading: setLoadingEvents, setRefreshing: setRefreshingEvents, setErr: setEventsError
    });
    swrFetch({
      loadFn: () => getFilteredAssignments({ endeavorId: courseIdNum, noEvent: true, completed: false }),
      current: rawAssignments, setCurrent: setRawAssignments, setPrev: setPrevAssignments,
      setLoading: setLoadingAssignments, setRefreshing: setRefreshingAssignments, setErr: setAssignmentsError
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIdNum, maxAssignments]);

  // Build cache key for assignments - use hydrated cache key (no size param = uses default from hydration)
  // getFilteredAssignments uses the full cache, so key is always the same
  const assignmentsCacheKey = useMemo(() => {
    const params = { page: 0, size: 10000 }; // Must match hydration size
    if (generateCacheKey) return generateCacheKey('user-assignments', params);
    return 'user-assignments_page-0_size-10000';
  }, [generateCacheKey]);

  // Direct cache fallbacks if hook has not yet resolved but cache already populated (or vice versa)
  const rawEventsEffective = useMemo(() => {
    if (Array.isArray(rawEvents)) return rawEvents;
    const cached = getCachedData ? getCachedData(`endeavor-events_${courseIdNum}`) : null;
    if (cached && Array.isArray(cached.data)) return cached.data;
    return rawEvents; // undefined or object
  }, [rawEvents, getCachedData, courseIdNum]);

  const rawAssignmentsEffective = useMemo(() => {
    if (Array.isArray(rawAssignments)) return rawAssignments;
    const cached = getCachedData ? getCachedData(assignmentsCacheKey) : null;
    if (cached && Array.isArray(cached.data)) return cached.data;
    return rawAssignments;
  }, [rawAssignments, getCachedData, assignmentsCacheKey]);

  const { events, assignments } = useMemo(() => {
    // Helper to coerce many possible response envelope shapes into a flat array
    const ensureArray = (root) => {
      if (!root) return [];
      if (Array.isArray(root)) return root;
      // BFS through limited depth to locate first non-empty array
      const visited = new Set();
      const queue = [root];
      const keyHints = ['content','items','data','results','events','assignments','value'];
      let depth = 0;
      while (queue.length && depth < 5) {
        const size = queue.length;
        for (let i=0;i<size;i++) {
          const node = queue.shift();
          if (!node || typeof node !== 'object' || visited.has(node)) continue;
          visited.add(node);
          if (Array.isArray(node) && node.length) return node;
          // direct hint keys first
            for (const k of keyHints) {
              if (Array.isArray(node[k]) && node[k].length) return node[k];
            }
          // push children objects/arrays
          Object.keys(node).forEach(k => {
            const child = node[k];
            if (Array.isArray(child)) {
              if (child.length) return child; // early exit inside forEach won't exit outer loops, so guard below
            }
            if (child && typeof child === 'object') queue.push(child);
          });
        }
        depth++;
      }
      // Heuristic: object whose values are homogeneous objects each with id/eventId may represent list
      if (root && typeof root === 'object') {
        const vals = Object.values(root);
        if (vals.length && vals.every(v => v && typeof v === 'object')) {
          // ensure at least some key like id/eventId/assignmentId exists among children
          const hasId = vals.some(v => v.id || v.eventId || v.assignmentId);
          if (hasId) return vals;
        }
      }
      return [];
    };
    const now = Date.now();
    const DAY_MS = 86400000;
    // Prefer current data when it has items; otherwise gracefully fall back to previousData (prevents empty flashes)
    const rawEvSource = (Array.isArray(rawEventsEffective) && rawEventsEffective.length > 0)
      ? rawEventsEffective
      : (Array.isArray(prevEvents) && prevEvents.length > 0 ? prevEvents : (rawEventsEffective || prevEvents));
  const evList = ensureArray(rawEvSource);
    let evFiltered = evList.filter(e => {
      const raw = pickEventDate(e);
      if (!raw) return true; // keep if unknown date (avoid dropping all)
      const dt = new Date(raw).getTime();
      return isNaN(dt) ? true : dt >= now - DAY_MS;
    });
    if (evFiltered.length === 0 && evList.length > 0) evFiltered = evList.slice();
    let ev = evFiltered.sort((a,b)=> {
      const da = new Date(pickEventDate(a)).getTime();
      const db = new Date(pickEventDate(b)).getTime();
      if (isNaN(da) && isNaN(db)) return 0; if (isNaN(da)) return 1; if (isNaN(db)) return -1; return da - db;
    });
    const future = ev.filter(e => new Date(pickEventDate(e)).getTime() >= now);
    const pastRecent = ev.filter(e => new Date(pickEventDate(e)).getTime() < now);
    ev = [...future, ...pastRecent].slice(0, maxEvents);

    // --- ASSIGNMENTS SOURCING STRATEGY ---
    // 1. Prefer direct user assignments (noEvent filter) for upcoming deliverables.
    // 2. If empty, attempt endeavor-level cached assignments (if present in cache outside of hook) or aggregate event-assignment caches.
    // 3. Deduplicate by assignmentId/id.
    let rawAsnSource = (Array.isArray(rawAssignmentsEffective) && rawAssignmentsEffective.length > 0)
      ? rawAssignmentsEffective
      : (Array.isArray(prevAssignments) && prevAssignments.length > 0 ? prevAssignments : (rawAssignmentsEffective || prevAssignments));
    let primaryAsnList = ensureArray(rawAsnSource);

    // If still empty, look for endeavor assignments cache key (older path) or event-assignment caches already fetched for visible events.
    if (primaryAsnList.length === 0 && getCachedData) {
      const endeavorAsnCache = getCachedData(`endeavor-assignments_${courseIdNum}`);
      if (endeavorAsnCache && Array.isArray(endeavorAsnCache.data)) {
        primaryAsnList = ensureArray(endeavorAsnCache.data);
      }
      // Aggregate any event-assignment caches (user may have multiple event assignment fetches already in cache)
      if (primaryAsnList.length === 0) {
        const eventDerived = [];
        if (Array.isArray(rawEventsEffective)) {
          rawEventsEffective.slice(0, maxEvents).forEach(ev => {
            const eid = ev.eventId || ev.id;
            if (!eid) return;
            const evCache = getCachedData(`event-assignments_${eid}`);
            if (evCache && Array.isArray(evCache.data) && evCache.data.length) {
              evCache.data.forEach(a => eventDerived.push(a));
            }
          });
        }
        if (eventDerived.length) primaryAsnList = eventDerived;
      }
    }

    // De-duplicate assignments (some may appear in multiple event caches or mixed sources)
    const dedup = () => {
      const seen = new Set();
      return (arr) => arr.filter(a => {
        const id = a && (a.assignmentId || a.id);
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    };
    primaryAsnList = dedup()(primaryAsnList);

    const asnList = primaryAsnList;
    // Explicitly filter out completed assignments
    let asnFiltered = asnList.filter(a => {
      // Filter out completed assignments (check both boolean and string values)
      if (a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed' || a.completionStatus === 'Completed') return false;
      const raw = pickDueDate(a);
      if (!raw) return true; // include undated
      const due = new Date(raw).getTime();
      return isNaN(due) ? true : due >= now - DAY_MS;
    }).sort((a,b)=> {
      const da = new Date(pickDueDate(a)).getTime();
      const db = new Date(pickDueDate(b)).getTime();
      if (isNaN(da) && isNaN(db)) return 0; if (isNaN(da)) return 1; if (isNaN(db)) return -1; return da - db;
    });
    if (asnFiltered.length === 0 && asnList.length > 0) asnFiltered = asnList.slice();
    const asnFuture = asnFiltered.filter(a => new Date(pickDueDate(a)).getTime() >= now);
    const asnPastRecent = asnFiltered.filter(a => new Date(pickDueDate(a)).getTime() < now);
    let asn = [...asnFuture, ...asnPastRecent].slice(0, maxAssignments);
    if (asn.length === 0 && asnList.length > 0) {
      asn = [...asnList]
        .filter(a => !!pickDueDate(a))
        .sort((a,b)=> new Date(pickDueDate(a)) - new Date(pickDueDate(b)))
        .slice(0, maxAssignments);
      if (asn.length === 0) {
        asn = asnList.slice(0, maxAssignments);
      }
    }
    const usedFallback = (Array.isArray(rawEventsEffective) && rawEventsEffective.length === 0 && Array.isArray(prevEvents) && prevEvents.length > 0)
      || (Array.isArray(rawAssignmentsEffective) && rawAssignmentsEffective.length === 0 && Array.isArray(prevAssignments) && prevAssignments.length > 0);
    if (process.env.NODE_ENV !== 'production') {
      if (!ev.length && (rawEvents || prevEvents)) {
        // eslint-disable-next-line no-console
        console.debug('[CourseCard] Events normalized empty', { rawEvents, prevEvents });
      }
      if (!asn.length && (rawAssignments || prevAssignments)) {
        // eslint-disable-next-line no-console
        console.debug('[CourseCard] Assignments normalized empty', { rawAssignments, prevAssignments });
      }
    }
    // FINAL RESCUE: If everything above produced empty arrays but raw sources clearly contain data, just take a shallow slice without any filtering.
    if (ev.length === 0 && Array.isArray(rawEvents) && rawEvents.length > 0) {
      ev = rawEvents.slice(0, maxEvents);
    }
    if (asn.length === 0 && Array.isArray(rawAssignments) && rawAssignments.length > 0) {
      asn = rawAssignments.slice(0, maxAssignments);
    }

    // FINAL safety: if still empty but we have event-level assignment caches, surface a couple to avoid blank state
    if (asn.length === 0 && getCachedData && Array.isArray(rawEventsEffective)) {
      const collected = [];
      rawEventsEffective.slice(0, maxEvents).forEach(ev => {
        const eid = ev.eventId || ev.id;
        const evCache = eid && getCachedData(`event-assignments_${eid}`);
        if (evCache && Array.isArray(evCache.data)) {
          evCache.data.forEach(a => {
            if (collected.length < maxAssignments) collected.push(a);
          });
        }
      });
      if (collected.length) asn = dedup()(collected).slice(0, maxAssignments);
    }

    const debugMeta = {
      rawEventsType: typeof rawEventsEffective,
      rawEventsIsArray: Array.isArray(rawEventsEffective),
      rawEventsLen: Array.isArray(rawEventsEffective) ? rawEventsEffective.length : undefined,
      rawAssignmentsType: typeof rawAssignmentsEffective,
      rawAssignmentsIsArray: Array.isArray(rawAssignmentsEffective),
      rawAssignmentsLen: Array.isArray(rawAssignmentsEffective) ? rawAssignmentsEffective.length : undefined,
      evListLen: evList.length,
      asnListLen: asnList.length,
      filteredEvents: ev.length,
      filteredAssignments: asn.length,
      usedRescueEvents: ev.length > 0 && evList.length === 0 && Array.isArray(rawEventsEffective) && rawEventsEffective.length > 0,
      usedRescueAssignments: asn.length > 0 && asnList.length === 0 && Array.isArray(rawAssignmentsEffective) && rawAssignmentsEffective.length > 0,
      cacheHitEvents: Array.isArray(rawEventsEffective) && !Array.isArray(rawEvents),
      cacheHitAssignments: Array.isArray(rawAssignmentsEffective) && !Array.isArray(rawAssignments),
      sourcedEndeavorAssignments: !!getCachedData && !!getCachedData(`endeavor-assignments_${courseIdNum}`),
      sourcedEventAssignments: asnList.length > 0 && rawAssignmentsEffective !== asnList
    };
    // Debug logging removed for production readiness
    return { events: ev, assignments: asn, hadFallback: usedFallback, debugMeta };
  }, [rawEvents, rawAssignments, rawEventsEffective, rawAssignmentsEffective, prevEvents, prevAssignments, pickEventDate, pickDueDate, maxEvents, maxAssignments, courseIdNum, getCachedData]);

  useEffect(() => {
    if (course && events && assignments && onLoaded) {
      onLoaded({ course, events, assignments });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, events.length, assignments.length]);

  // Track which eventIds have been processed (fetched or adopted from cache) to prevent repeated calls
  const processedEventAssignmentIdsRef = React.useRef(new Set());

  // Fetch assignments for each event (limited) once events are loaded
  useEffect(() => {
    let cancelled = false;
    async function loadEventAssignmentsIncremental() {
      if (!events || events.length === 0) return;
      const newResults = {};
      const toFetch = [];
      // Determine per-event strategy: adopt from global cache or schedule fetch
      events.forEach(ev => {
        const evId = ev && (ev.eventId || ev.id);
        if (!evId) return;
        // Skip if already in local state or previously processed
        if (Object.prototype.hasOwnProperty.call(eventAssignments, evId) || processedEventAssignmentIdsRef.current.has(evId)) return;
        // Attempt direct global cache adoption first (avoids calling getAssignmentsByEvent which still logs cached hits)
        const globalCache = getCachedData && getCachedData(`event-assignments_${evId}`);
        const cachedArr = globalCache && Array.isArray(globalCache.data) ? globalCache.data : null;
        if (cachedArr && cachedArr.length) {
          newResults[evId] = cachedArr;
          processedEventAssignmentIdsRef.current.add(evId);
        } else {
          toFetch.push(evId);
        }
      });

      // Batch fetch only truly missing IDs
      if (toFetch.length) {
        await Promise.all(toFetch.map(async evId => {
          try {
            const data = await getAssignmentsByEvent(evId);
            const list = Array.isArray(data) ? data : (data?.content || []);
            newResults[evId] = list;
            processedEventAssignmentIdsRef.current.add(evId);
          } catch { /* ignore per-event errors */ }
        }));
      }

      if (!cancelled && Object.keys(newResults).length) {
        setEventAssignments(prev => ({ ...prev, ...newResults }));
      }
    }
    loadEventAssignmentsIncremental();
    return () => { cancelled = true; };
  }, [events, getAssignmentsByEvent, eventAssignments, getCachedData]);

  // Event assignments will be fetched separately (not yet implemented here)
  const assignmentsByEvent = eventAssignments; // rename for existing rendering logic

  // Normalize possible wrapped course structures (array, {data:{...}}, {content:[...]})
  const effectiveCourse = useMemo(() => {
    const unwrap = (obj) => {
      if (!obj) return null;
      if (Array.isArray(obj)) return obj[0] || null;
      if (obj.data && typeof obj.data === 'object') return unwrap(obj.data);
      if (Array.isArray(obj.content)) return unwrap(obj.content[0]);
      return obj;
    };
    return unwrap(course) || unwrap(prevCourse);
  }, [course, prevCourse]);
  const meta = useMemo(() => {
    if (!effectiveCourse) return {};
    const md = Array.isArray(effectiveCourse.metadata) ? effectiveCourse.metadata : [];
    const findVal = (label) => {
      const entry = md.find(m => (m.metadataName||'').toLowerCase() === label);
      return (entry?.value || '').toString().trim() || null;
    };
    return {
      professor: findVal('instructor name') || findVal('professor') || findVal('instructor'),
      room: findVal('course location') || findVal('room') || findVal('room number'),
      semester: findVal('semester'),
      meeting: findVal('meeting times') || findVal('schedule pattern')
    };
  }, [effectiveCourse]);

  const goToZoom = () => {
    try {
      const el = document.querySelector(`.course-card[data-id='${courseIdNum}']`) || cardRef.current;
      if (el) {
        el.classList.add('zooming');
        setTimeout(()=> navigate(`/course-card/${courseIdNum}`), 140);
      } else {
        navigate(`/course-card/${courseIdNum}`);
      }
    } catch { navigate(`/course-card/${courseIdNum}`); }
  };
  const goToEvent = (eventId) => navigate(`/event/${eventId}`);

  const formatDateShort = (dStr) => {
    if (!dStr) return '';
    try {
      const d = new Date(dStr);
      if (isNaN(d)) return '';
      return d.toLocaleDateString(undefined,{ month:'short', day:'numeric'});
    } catch { return ''; }
  };
  const formatTime = (dStr) => {
    if (!dStr) return '';
    try {
      const d = new Date(dStr);
      if (isNaN(d)) return '';
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit'});
    } catch { return ''; }
  };

  /**
   * Format a raw room/location string for display on the compact CourseCard only.
   * Does not mutate input or stored data — display-only.
   * Rules (display-only):
   *  - null/empty -> return ""
   *  - strip leading labels like "Room:", "Location:", "Rm"
   *  - remove trailing parenthetical text starting at '('
   *  - split on separators like `;`, `,`, `|`, long dashes, or ` - ` and take first segment
   *  - collapse multiple spaces and trim
   */
  const formatRoomForCard = (rawRoom) => {
    let s = String(rawRoom || '').trim();
    if (!s) return '';
    // Remove leading labels (case-insensitive)
    s = s.replace(/^\s*(?:room|location|rm|r\.m\.?|room:|location:)\s*[:\-.\s]*/i, '');
    // Remove trailing parenthetical and anything after
    s = s.replace(/\s*\(.*$/, '');
    // Split on common separators and keep first chunk. Only split on space-dash-space so hyphenated rooms like B-12 are preserved.
    const parts = s.split(/\s*(?:;|,|\||–|—|\s-\s)\s*/);
    s = (parts[0] || '').trim();
    // Collapse internal whitespace
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  };

  const formatDateTimeShort = (dStr) => {
    if (!dStr) return '';
    try {
      const d = new Date(dStr);
      if (isNaN(d)) return '';
      return d.toLocaleDateString(undefined,{ month:'short', day:'numeric'}) + ' ' + d.toLocaleTimeString([], { hour:'numeric', minute:'2-digit'});
    } catch { return ''; }
  };

  const extractDescription = (a) => {
    if (!a) return '';
    return a.description || a.details || a.summary || a.notes || '';
  };

  const cardRef = React.useRef(null);
  const loadingAny = loadingCourse || loadingEvents || loadingAssignments;
  const refreshing = (!loadingAny) && (refreshingCourse || refreshingEvents || refreshingAssignments); // eslint-disable-line no-unused-vars

  // Utility: deduplicate assignments by stable id (assignmentId || id)
  const dedupeAssignmentsById = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    return arr.filter(a => {
      const id = a && (a.assignmentId || a.id);
      if (id == null) return true; // allow items without id (will get index key)
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, []);

  // Display-only formatted room for the CourseCard subtitle
  const displayRoom = formatRoomForCard(meta.room);

  return (
    <div ref={cardRef} data-id={courseIdNum} className={`course-card ${className}`}>      
      <div className="course-card__header">
        {loadingCourse && !course && <div className="course-card__loading">Loading…</div>}
        {courseError && <div className="course-card__error">Failed to load</div>}
        {effectiveCourse && (
          <>
            <div className="course-card__title-row">
              <h3 className="course-card__title" title={effectiveCourse.name || effectiveCourse.title || effectiveCourse.courseName}>{effectiveCourse.name || effectiveCourse.title || effectiveCourse.courseName || 'Course'}</h3>
              {meta.semester && <span className="course-card__chip" title="Semester">{meta.semester}</span>}
              {refreshingCourse && <span className="course-card__chip pulse" title="Refreshing">↻</span>}
            </div>
            <div className="course-card__meta-line">
              {meta.professor && <span title="Professor">👤 {meta.professor}</span>}
              {displayRoom ? <span title="Location">🏫 {displayRoom}</span> : null}
              {meta.meeting && <span title="Meeting Pattern">🕒 {meta.meeting}</span>}
            </div>
          </>
        )}
        {!effectiveCourse && !loadingCourse && (
          <div className="course-card__error" style={{fontSize:'0.65rem', opacity:0.8}}>No course meta</div>
        )}
      </div>
      <div className="course-card__body">
        <div className="course-card__events course-card__section">
          <div className="course-card__row-label">Events</div>
          <div className="course-card__event-cards">
            {(() => {
              const cards = [];
              const show = events.slice(0,2);
              for (let i=0;i<2;i++) {
                const ev = show[i];
                if (ev) {
                  const id = ev.eventId || ev.id || i;
                  const start = ev.startTime || ev.startDate || ev.startDatetime || ev.startDateTime || ev.eventDate || ev.date;
                  const dateStr = formatDateShort(start);
                  const timeStr = formatTime(start);
                  const evAssignments = assignmentsByEvent[id] || [];
                  // Use composite key: id + index to ensure uniqueness even if event IDs duplicate
                  const uniqueKey = id != null ? `event-${id}-${i}` : `event-idx-${i}`;
                  cards.push(
                    <motion.div
                      key={uniqueKey}
                      className="cc-event-square"
                      title={ev.title || ev.name}
                      onClick={()=>goToEvent(id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e)=>{ if(e.key==='Enter') goToEvent(id); }}
                      initial={{ y:0, boxShadow:'0 0 0 rgba(0,0,0,0.0)' }}
                      whileHover={{ y:-2, boxShadow:'0 4px 10px -2px rgba(0,0,0,0.12)' }}
                      transition={{ type:'spring', stiffness:240, damping:24 }}
                    >
                      <div className="cc-event-square-inner cc-event-has-assignments">
                        <div className="cc-event-square-header">
                          <div className="cc-event-square-title-row single-line">{ev.title || ev.name || 'Event'}</div>
                          <div className="cc-event-square-datetime-row single-line">{dateStr || '—'} {timeStr}</div>
                        </div>
                        {evAssignments.length > 0 && (
                          <ul className="cc-event-square-asnlist">
                            {dedupeAssignmentsById(evAssignments).slice(0,12).map((a,i) => {
                              const aId = a.assignmentId || a.id;
                              const title = a.title || a.name || 'Assignment';
                              return (
                                <li key={aId != null ? `asn-${aId}` : `asn-idx-${i}`} className="cc-event-square-asnitem single-line" title={title}>{title}</li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </motion.div>
                  );
                } else if (!loadingAny) {
                  cards.push(
                    <div key={"placeholder-"+i} className="cc-event-square placeholder">
                      <div className="cc-event-square-inner">
                        <div className="cc-event-square-date">&nbsp;</div>
                        <div className="cc-event-square-title muted">No Event</div>
                        <div className="cc-event-square-time">&nbsp;</div>
                      </div>
                    </div>
                  );
                } else {
                  cards.push(
                    <div key={"loading-"+i} className="cc-event-square loading">
                      <div className="cc-event-square-inner">
                        <div className="cc-skeleton line short" />
                        <div className="cc-skeleton line" />
                        <div className="cc-skeleton line tiny" />
                      </div>
                    </div>
                  );
                }
              }
              return cards;
            })()}
          </div>
        </div>
        <div className="course-card__assignments course-card__section">
          <div className="course-card__row-label">Incomplete Assignments</div>
          <div className="course-card__asn-cards" aria-label="Course Deliverables">
            {loadingAssignments && assignments.length === 0 && (
              <div className="cc-asn-card loading" aria-hidden="true">
                <div className="cc-asn-card-inner">
                  <div className="cc-skeleton line short" />
                  <div className="cc-skeleton line" />
                </div>
              </div>
            )}
            {!loadingAssignments && assignments.length === 0 && (
              <div className="cc-asn-empty">No deliverables</div>
            )}
            {assignments.map((asn, idx) => {
              const id = asn.assignmentId || asn.id;
              const dueRaw = asn.dueDatetime || asn.dueDateTime || asn.dueDate || asn.due_at || asn.dateDue;
              const dueLabel = formatDateTimeShort(dueRaw) || formatDateShort(dueRaw) || '—';
              const title = asn.title || asn.name || 'Assignment';
              const desc = extractDescription(asn); // eslint-disable-line no-unused-vars
              // Calculate urgency for color coding
              let urgency = 'normal';
              if (dueRaw) {
                const dueDate = new Date(dueRaw);
                const now = new Date();
                const hoursUntilDue = (dueDate - now) / (1000 * 60 * 60);
                if (hoursUntilDue < 0) urgency = 'overdue';
                else if (hoursUntilDue < 24) urgency = 'urgent';
                else if (hoursUntilDue < 72) urgency = 'upcoming';
              }
              // Use composite key: id (if available) + index to ensure uniqueness even if IDs duplicate
              const uniqueKey = id != null ? `asn-${id}-${idx}` : `asn-idx-${idx}`;
              
              const handleCompleteClick = async (e) => {
                e.stopPropagation(); // Prevent navigation to assignment
                if (!id) return;
                try {
                  console.log('[CourseCard] Marking assignment complete:', id);
                  await markAssignmentCompleted(id);
                  
                  // Clear cache to force fresh data
                  clearCache('assignments');
                  clearCache('user-assignments');
                  
                  // Small delay to allow backend to process
                  await new Promise(resolve => setTimeout(resolve, 150));
                  
                  // Refetch assignments to update the list
                  console.log('[CourseCard] Refetching assignments for endeavor:', courseIdNum);
                  const freshAssignments = await getFilteredAssignments({ endeavorId: courseIdNum, noEvent: true, completed: false });
                  console.log('[CourseCard] Fresh assignments returned:', freshAssignments?.length, freshAssignments?.map(a => ({
                    id: a.id || a.assignmentId,
                    title: a.title,
                    completed: a.completed,
                    status: a.status,
                    submissionStatus: a.submissionStatus,
                    completionStatus: a.completionStatus
                  })));
                  setRawAssignments(freshAssignments);
                } catch (error) {
                  console.error('Failed to mark assignment complete:', error);
                }
              };
              
              return (
                <div key={uniqueKey} className={`cc-asn-card cc-asn-${urgency}`} onClick={()=> id && navigate(`/assignment/${id}`)} title={title}>
                  <div className="cc-asn-card-inner">
                    <div className="cc-asn-title two-line">{title}</div>
                    <div className="cc-asn-due single-line">{dueLabel}</div>
                    <button 
                      className="cc-asn-complete-btn"
                      onClick={handleCompleteClick}
                      title="Mark as complete"
                      aria-label="Mark assignment as complete"
                    >
                      ✓
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="cc-zoom-btn-floating"
        onClick={goToZoom}
        title="Zoom into immersive view"
        aria-label="Zoom into immersive view"
      >
        <span aria-hidden="true">⤢</span>
      </button>
      {/* Debug overlay removed */}
    </div>
  );
}

export default React.memo(CourseCard);
